import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Music, Plus, RefreshCw, Save, Trash2, Upload } from 'lucide-react';

import { Button } from '../components_dashboard/ui/button';
import { Input } from '../components_dashboard/ui/input';
import { Switch } from '../components_dashboard/ui/switch';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;
const AMBIENT_SOUND_TYPE = 'ambient_sound';
const THINKING_SOUND_TYPE = 'thinking_sound';

function revokePlayableUrls(items = []) {
  items.forEach((item) => {
    const playableUrl = String(item?.playable_url || '').trim();
    if (playableUrl.startsWith('blob:')) {
      URL.revokeObjectURL(playableUrl);
    }
  });
}

function CreateOptionForm({ type, label, onCreate, onCancel }) {
  const [name, setName] = useState('');
  const [active, setActive] = useState(true);
  const [audioFile, setAudioFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const resetForm = () => {
    setName('');
    setActive(true);
    setAudioFile(null);
    setPreviewUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return '';
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (event) => {
    const nextFile = event.target.files?.[0] || null;
    setAudioFile(nextFile);
    setPreviewUrl((current) => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return nextFile ? URL.createObjectURL(nextFile) : '';
    });
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    if (!audioFile) {
      toast.error('Please choose an MP3 file');
      return;
    }

    setUploading(true);
    const created = await onCreate({
      name: name.trim(),
      type,
      active,
      audioFile,
    });
    setUploading(false);

    if (created) {
      resetForm();
      onCancel();
    }
  };

  return (
    <div className="mb-4 rounded-2xl border border-violet-100 bg-violet-50/60 p-4">
      <div className="grid gap-3">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr),auto]">
          <Input
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="bg-white"
            placeholder={`${label} name`}
          />
          <label className="flex min-h-10 cursor-pointer items-center justify-center rounded-md border border-dashed border-violet-200 bg-white px-4 text-sm font-medium text-violet-700 hover:bg-violet-50">
            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,audio/mpeg"
              className="hidden"
              onChange={handleFileChange}
            />
            {audioFile ? audioFile.name : 'Choose MP3'}
          </label>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">{active ? 'Active' : 'Inactive'}</span>
            <Switch checked={active} onCheckedChange={setActive} />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                resetForm();
                onCancel();
              }}
              disabled={uploading}
              className="text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={uploading}
              className="bg-violet-600 text-white hover:bg-violet-700"
            >
              {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
              Upload {label}
            </Button>
          </div>
        </div>

        {previewUrl ? (
          <div className="rounded-xl border border-white/80 bg-white p-3">
            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Preview Before Save</p>
            <audio controls preload="metadata" className="w-full" src={previewUrl}>
              Your browser does not support audio playback.
            </audio>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function OptionItem({ item, onUpdate, onDelete, onUpload }) {
  const [draft, setDraft] = useState({ ...item });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setDraft({ ...item });
  }, [item]);

  const hasChanges = draft.name !== item.name || draft.active !== item.active;
  const audioSrc = item.playable_url || item.preview_url || item.background_url || '';

  const handleSave = async () => {
    if (!draft.name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }

    setSaving(true);
    await onUpdate(item.id, { name: draft.name.trim(), active: draft.active });
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this option?')) {
      return;
    }

    setDeleting(true);
    await onDelete(item.id);
    setDeleting(false);
  };

  const handleFileChange = async (event) => {
    const nextFile = event.target.files?.[0] || null;
    if (!nextFile) {
      return;
    }

    setUploading(true);
    await onUpload(item.id, nextFile);
    setUploading(false);
    event.target.value = '';
  };

  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
        <Input
          value={draft.name}
          onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          className="flex-1 bg-white"
          placeholder="Name"
        />

        <div className="flex items-center gap-2 px-2">
          <span className="text-xs text-slate-500">{draft.active ? 'Active' : 'Inactive'}</span>
          <Switch
            checked={draft.active}
            onCheckedChange={(checked) => setDraft({ ...draft, active: checked })}
          />
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".mp3,audio/mpeg"
          className="hidden"
          onChange={handleFileChange}
        />

        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="border-slate-200 bg-white text-slate-700 hover:bg-slate-100"
        >
          {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          {item.background_url ? 'Replace MP3' : 'Upload MP3'}
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={hasChanges ? 'bg-violet-50 text-violet-600 hover:text-violet-700' : 'text-slate-400'}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        </Button>

        <Button
          variant="ghost"
          size="icon"
          onClick={handleDelete}
          disabled={deleting}
          className="text-red-500 hover:bg-red-50 hover:text-red-600"
        >
          {deleting ? <Loader2 className="h-4 w-4 animate-spin text-red-500" /> : <Trash2 className="h-4 w-4" />}
        </Button>
      </div>

      <div className="mt-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
          <span>{item.source_file_name ? `File: ${item.source_file_name}` : 'No audio uploaded yet'}</span>
          {item.bucket_name ? (
            <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] text-slate-500">{item.bucket_name}</span>
          ) : null}
        </div>

        {audioSrc ? (
          <audio key={audioSrc} controls preload="auto" className="w-full" src={audioSrc}>
            Your browser does not support audio playback.
          </audio>
        ) : null}
      </div>
    </div>
  );
}

export default function BackgroundVoiceDashboard() {
  const { token } = useAuth();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composerOpen, setComposerOpen] = useState({
    ambient_sound: false,
    thinking_sound: false,
  });
  const optionsRef = useRef([]);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const hydratePlayableAudioUrls = useCallback(async (rows) => {
    const normalizedRows = Array.isArray(rows) ? rows : [];

    const hydratedRows = await Promise.all(
      normalizedRows.map(async (row) => {
        const rowId = String(row?.id || '').trim();
        if (!rowId) {
          return row;
        }

        try {
          const response = await axios.get(`${apiUrl}/api/v1/admin/background-voices/${rowId}/audio`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
          });
          return {
            ...row,
            playable_url: URL.createObjectURL(response.data),
          };
        } catch (error) {
          console.error('Failed to fetch playable audio for row', rowId, error);
          return {
            ...row,
            playable_url: row.preview_url || row.background_url || '',
          };
        }
      })
    );

    setOptions((current) => {
      revokePlayableUrls(current);
      return hydratedRows;
    });
  }, [token]);

  const fetchOptions = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/api/v1/admin/background-voices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = Array.isArray(response.data?.background_voices) ? response.data.background_voices : [];
      await hydratePlayableAudioUrls(rows);
    } catch (error) {
      console.error('Failed to fetch background options', error);
      toast.error(error.response?.data?.detail || 'Failed to load options.');
    } finally {
      setLoading(false);
    }
  }, [hydratePlayableAudioUrls, token]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  useEffect(() => {
    return () => {
      revokePlayableUrls(optionsRef.current);
    };
  }, []);

  const handleCreateUpload = async ({ name, type, active, audioFile }) => {
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('type', type);
      formData.append('active', String(active));
      formData.append('audio_file', audioFile);

      await axios.post(`${apiUrl}/api/v1/admin/background-voices/upload`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchOptions();
      toast.success('Audio option created');
      return true;
    } catch (error) {
      console.error('Failed to create background option', error);
      toast.error(error.response?.data?.detail || 'Failed to upload audio.');
      return false;
    }
  };

  const handleUpdate = async (id, payload) => {
    try {
      await axios.patch(`${apiUrl}/api/v1/admin/background-voices/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchOptions();
      toast.success('Option updated');
    } catch (error) {
      console.error('Failed to update background option', error);
      toast.error(error.response?.data?.detail || 'Failed to update option.');
    }
  };

  const handleUpload = async (id, audioFile) => {
    try {
      const formData = new FormData();
      formData.append('audio_file', audioFile);
      await axios.post(`${apiUrl}/api/v1/admin/background-voices/${id}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchOptions();
      toast.success('Audio uploaded');
    } catch (error) {
      console.error('Failed to upload audio file', error);
      toast.error(error.response?.data?.detail || 'Failed to upload audio.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${apiUrl}/api/v1/admin/background-voices/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchOptions();
      toast.success('Option deleted');
    } catch (error) {
      console.error('Failed to delete background option', error);
      toast.error(error.response?.data?.detail || 'Failed to delete option.');
    }
  };

  const ambientSoundOptions = options.filter((option) => option.type === AMBIENT_SOUND_TYPE);
  const thinkingSoundOptions = options.filter((option) => option.type === THINKING_SOUND_TYPE);

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-slate-50">
      

      <main className="min-h-0 flex-1 overflow-y-auto p-6  lg:p-8">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-violet-100 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                <Music className="h-3.5 w-3.5" />
                Audio Settings
              </div>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Background Audio</h1>
              <p className="mt-1 text-slate-500">
                Manage ambient sound and thinking sound tracks available to users system-wide.
              </p>
            </div>

            <Button
              variant="outline"
              onClick={fetchOptions}
              disabled={loading}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Refresh Options
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            <div className="flex min-h-[500px] flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-6 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Ambient Sound</h2>
                  <p className="text-sm text-slate-500">Environmental sound beds like cafe, office, rain, or fan noise.</p>
                </div>
                <Button
                  onClick={() => setComposerOpen((current) => ({ ...current, [AMBIENT_SOUND_TYPE]: !current[AMBIENT_SOUND_TYPE] }))}
                  variant="outline"
                  className="border-violet-200 text-violet-700 hover:bg-violet-50"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Ambient Sound
                </Button>
              </div>

              {composerOpen[AMBIENT_SOUND_TYPE] ? (
                <CreateOptionForm
                  type={AMBIENT_SOUND_TYPE}
                  label="Ambient Sound"
                  onCreate={handleCreateUpload}
                  onCancel={() => setComposerOpen((current) => ({ ...current, [AMBIENT_SOUND_TYPE]: false }))}
                />
              ) : null}

              {loading ? (
                <div className="flex flex-1 items-center justify-center text-slate-500">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : ambientSoundOptions.length === 0 ? (
                <div className="flex flex-1 items-center justify-center text-sm italic text-slate-400">
                  No ambient sound options have been added yet
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {ambientSoundOptions.map((item) => (
                    <OptionItem
                      key={item.id}
                      item={item}
                      onUpdate={handleUpdate}
                      onDelete={handleDelete}
                      onUpload={handleUpload}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="flex min-h-[500px] flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="mb-6 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Thinking Sound</h2>
                  <p className="text-sm text-slate-500">Soft music or thinking tracks like jazz, lofi, or instrumental loops.</p>
                </div>
                <Button
                  onClick={() => setComposerOpen((current) => ({ ...current, [THINKING_SOUND_TYPE]: !current[THINKING_SOUND_TYPE] }))}
                  variant="outline"
                  className="border-violet-200 text-violet-700 hover:bg-violet-50"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Thinking Sound
                </Button>
              </div>

              {composerOpen[THINKING_SOUND_TYPE] ? (
                <CreateOptionForm
                  type={THINKING_SOUND_TYPE}
                  label="Thinking Sound"
                  onCreate={handleCreateUpload}
                  onCancel={() => setComposerOpen((current) => ({ ...current, [THINKING_SOUND_TYPE]: false }))}
                />
              ) : null}

              {loading ? (
                <div className="flex flex-1 items-center justify-center text-slate-500">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : thinkingSoundOptions.length === 0 ? (
                <div className="flex flex-1 items-center justify-center text-sm italic text-slate-400">
                  No thinking sound options have been added yet
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {thinkingSoundOptions.map((item) => (
                    <OptionItem
                      key={item.id}
                      item={item}
                      onUpdate={handleUpdate}
                      onDelete={handleDelete}
                      onUpload={handleUpload}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
